import type { NotificationType } from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import Icon from '../icon/Icon.web';
import './notification.web.css';

export interface NotificationProps {
  id?: string;
  type?: NotificationType;
  message: string;
  buttonText?: string;
  buttonAction?: () => void;
  showCloseIcon?: boolean;
  autoClose?: boolean;
  autoCloseDelay?: number;
  onClose?: (id?: string) => void;
  className?: string;
}

const Notification: React.FC<NotificationProps> = ({
  id,
  type = 'default',
  message,
  buttonText,
  buttonAction,
  showCloseIcon = true,
  autoClose = true,
  autoCloseDelay = 2000,
  onClose,
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = React.useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.(id);
    }, 300);
  }, [id, onClose]);

  useEffect(() => {
    if (autoClose && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay, handleClose]);

  const handleButtonClick = () => {
    buttonAction?.();
    handleClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={clsx(
        'cometchat-calls-notification',
        `cometchat-calls-notification--${type}`,
        className,
      )}
      role="alert"
      aria-live="polite">
      <div className="cometchat-calls-notification__message">{message}</div>
      {buttonText && buttonAction && (
        <button
          className="cometchat-calls-notification__button"
          onClick={handleButtonClick}
          aria-label={`${buttonText} - ${message}`}
          type="button">
          {buttonText}
        </button>
      )}
      {showCloseIcon && (
        <button
          className="cometchat-calls-notification__close"
          onClick={handleClose}
          aria-label="Close notification"
          type="button">
          <Icon name="close" size={24} fill="#858585" />
        </button>
      )}
    </div>
  );
};

export default Notification;
